export enum SupplierId {
  IMPORTED = 1,
  PHARMACY = 18,
  SUPPLEMENTS = 27
}

export enum THCConcentration {
  WITHOUT_THC = 1, // "Menor que 0,3%"
  WITH_THC = 2 // "Maior que 0,3%"
}

export enum ShapeId {
  OIL = 1, // "Óleo"
  CAPSULE = 2, // "Cápsula"
  LOTION = 3, // "Loção"
  CREAM = 4, // "Creme"
  GUM = 6, // "Goma"
  SPRAY = 7, // "Spray"
  ROLL_ON = 8, // "Roll-On"
  GEL = 10, // "Gel"
  BALM = 28, // "Balm"
  FLOWER = 29, // "Flor" - Critical for business logic
  SERUM = 30 // "Sérum"
}

export enum ClassId {
  SUPPLEMENT = 6, // "Suplemento" - Critical for business logic
  MUSHROOM = 7, // "Cogumelo" - Critical for business logic
  PHARMACY_OTHERS = 4, // "Outros" - Critical for business logic
  GENETIC_TEST = 8, // "Teste Genético"
  /**
   * @deprecated Nao use estes dois. Sao classes criadas pela sincronizacao do
   * catalogo, entao o id e gerado pelo banco de cada ambiente -- em producao
   * valem 10 e 9, nao 17 e 16, e o filtro devolve lista vazia com o numero
   * errado. Resolva pelo nome com `useProductClassId`. Mantidos apenas para
   * nao quebrar imports existentes.
   */
  ALLOPATHIC = 17,
  /** @deprecated ver ALLOPATHIC acima */
  HYGIENE_BEAUTY = 16
}

/**
 * Filter types based on actual implementation analysis
 */
export type TProductFilterType =
  | 'supplier_id'
  | 'brand_id'
  | 'shape_id'
  | 'class_id'
  | 'category_id'
  | 'cbd_concentration'
  | 'thc_concentration'
  | 'pathology'
  | 'veiculo' // Prescriber only
  | 'extraction_method' // Prescriber only
  | 'gmp' // Prescriber only
  | 'curatorship_range'
  | 'volume_name'
  | 'cannabinoid_name'
  | 'proportion_id'
  | 'max_price'

/**
 * Filter display configuration based on current NewFilters implementation
 */
export const PRODUCT_FILTERS_CONFIG: Record<
  TProductFilterType,
  {
    label: string
    curatorshipLabel?: string // Different label for prescribers
    isPrescriberOnly?: boolean
    defaultOpen?: boolean
    subtitle?: string
    isNew?: boolean
  }
> = {
  supplier_id: {
    label: 'Origem do produto',
    defaultOpen: true // Currently opens by default
  },
  brand_id: {
    label: 'Marca'
  },
  shape_id: {
    label: 'Forma'
  },
  class_id: {
    label: 'Classe'
  },
  category_id: {
    label: 'Categoria'
  },
  cbd_concentration: {
    label: 'Concentração de CBD'
  },
  thc_concentration: {
    label: 'Concentração de THC'
  },
  cannabinoid_name: {
    label: 'Canabinoide'
  },
  proportion_id: {
    label: 'Proporção'
  },
  volume_name: {
    label: 'Volume'
  },
  max_price: {
    label: 'Preço máximo'
  },
  pathology: {
    label: 'pathology'
  },
  veiculo: {
    label: 'Veiculo',
    isPrescriberOnly: true,
    isNew: true
  },
  extraction_method: {
    label: 'Método de Extração',
    isPrescriberOnly: true,
    isNew: true
  },
  gmp: {
    label: 'GMP',
    subtitle: 'Boas praticas de fabricação',
    isPrescriberOnly: true,
    isNew: true
  },
  curatorship_range: {
    label: 'Aderência à Curadoria',
    subtitle: 'Aderência aos critérios da Curadoria',
    isPrescriberOnly: true,
    isNew: true
  }
}

export const FLOWER_SHAPE_ID = ShapeId.FLOWER.toString()
export const SUPPLEMENT_CLASS_ID = ClassId.SUPPLEMENT.toString()
export const NATIONAL_SUPPLIER_ID = SupplierId.PHARMACY.toString()

/**
 * Filter order based on current implementation priority
 * (excludes 'routeofadministrations' and 'shapes' which are filtered out)
 */
export const FILTER_DISPLAY_ORDER: TProductFilterType[] = [
  'supplier_id',
  'brand_id',
  'shape_id',
  'class_id',
  'cannabinoid_name',
  'proportion_id',
  'volume_name',
  'max_price'
  // 'category_id',
  // 'cbd_concentration',
  // 'thc_concentration',
  // 'cannabidiol_id'
  // 'pathology',
  // 'veiculo', // Prescriber only
  // 'extraction_method', // Prescriber only
  // 'gmp' // Prescriber only
]

export const TOPICAL_SHAPES = [ShapeId.CREAM, ShapeId.LOTION, ShapeId.GEL, ShapeId.ROLL_ON, ShapeId.BALM, ShapeId.SERUM]

export const ProductFilters = {
  IMPORTED_ALL: {
    supplier_id: SupplierId.IMPORTED,
    translated_name: ''
  },
  IMPORTED_WITH_THC: {
    supplier_id: SupplierId.IMPORTED,
    thc_concentration: THCConcentration.WITH_THC,
    translated_name: ''
  },
  IMPORTED_WITHOUT_THC: {
    supplier_id: SupplierId.IMPORTED,
    thc_concentration: THCConcentration.WITHOUT_THC,
    translated_name: ''
  },
  IMPORTED_OILS: {
    supplier_id: SupplierId.IMPORTED,
    shape_id: ShapeId.OIL,
    translated_name: ''
  },
  IMPORTED_GUMS: {
    supplier_id: SupplierId.IMPORTED,
    shape_id: ShapeId.GUM,
    translated_name: ''
  },
  ASSOCIATIONS: {
    translated_name: '',
    delivery_by_association: true
  },
  IMPORTED_TOPICALS: {
    supplier_id: SupplierId.IMPORTED,
    shape_id: TOPICAL_SHAPES,
    translated_name: ''
  },
  NACIONAL_ALL: {
    supplier_id: SupplierId.PHARMACY,
    translated_name: ''
  },

  PHARMACY_ALL: {
    supplier_id: SupplierId.PHARMACY,
    translated_name: ''
  },
  PHARMACY_WITH_THC: {
    supplier_id: SupplierId.PHARMACY,
    thc_concentration: THCConcentration.WITH_THC,
    translated_name: ''
  },
  PHARMACY_WITHOUT_THC: {
    supplier_id: SupplierId.PHARMACY,
    thc_concentration: THCConcentration.WITHOUT_THC,
    translated_name: ''
  },
  PHARMACY_OILS: {
    supplier_id: SupplierId.PHARMACY,
    shape_id: ShapeId.OIL,
    translated_name: ''
  },

  // Supplements
  SUPPLEMENTS_ALL: {
    class_id: [ClassId.SUPPLEMENT, ClassId.MUSHROOM],
    translated_name: ''
  },
  SUPPLEMENTS_PEA: {
    supplier_id: SupplierId.SUPPLEMENTS,
    translated_name: 'PEA'
  },
  SUPPLEMENTS_CURCUMIN: {
    supplier_id: SupplierId.SUPPLEMENTS,
    translated_name: 'Curcumina'
  },
  SUPPLEMENTS_MELATONIN: {
    supplier_id: SupplierId.SUPPLEMENTS,
    translated_name: 'Melatonina'
  },
  SUPPLEMENTS_CREATINE: {
    supplier_id: SupplierId.SUPPLEMENTS,
    translated_name: 'Creatina'
  },

  MUSHROOMS_ALL: {
    category_slug: 'foco-e-performance'
  },
  MUSHROOMS_OILS: {
    translated_name: 'cogumelo',
    shape_id: ShapeId.OIL
  },

  IMPORTED_OTHERS: {
    supplier_id: SupplierId.IMPORTED,
    translated_name: ''
  },
  PHARMACY_OTHERS: {
    class_id: ClassId.PHARMACY_OTHERS,
    translated_name: ''
  },
  SUPPLEMENTS_OTHERS: {
    supplier_id: SupplierId.SUPPLEMENTS,
    translated_name: ''
  },
  MUSHROOMS_OTHERS: {
    translated_name: 'cogumelo'
  },

  GENETIC_TEST_ALL: {
    class_id: ClassId.GENETIC_TEST,
    translated_name: ''
  },

  ALLOPATHIC_ALL: {
    class_id: ClassId.ALLOPATHIC,
    translated_name: ''
  },

  HYGIENE_BEAUTY_ALL: {
    class_id: ClassId.HYGIENE_BEAUTY,
    translated_name: ''
  }
} as const

/**
 * Helper to build URL from filter object
 */
export const buildFilterUrl = (
  filters:
    | {
        supplier_id?: number
        thc_concentration?: number
        shape_id?: number | number[]
        class_id?: number | number[]
        translated_name?: string
        limit?: number
      }
    | (typeof ProductFilters)[keyof typeof ProductFilters]
) => {
  const params = new URLSearchParams()

  const limit = 'limit' in filters ? filters.limit || 16 : 16
  params.append('limit', limit.toString())

  Object.entries(filters as Record<string, any>).forEach(([key, value]) => {
    if (key === 'limit') return

    if (Array.isArray(value)) {
      value.forEach((v) => params.append(key, v.toString()))
    } else if (value !== undefined) {
      params.append(key, value.toString())
    }
  })

  return `/produtos/1?${params.toString()}`
}
